/**
 * Stripe Connect Embedded Components
 *
 * Initializes ConnectJS for rendering Stripe's embedded UI components
 * (payments list, dispute management, notification banner) within our portal.
 */
import { loadConnectAndInitialize } from '@stripe/connect-js';
import api from './api';

let _instance = null;
let _publishableKey = null;

/**
 * Gets or creates a ConnectJS instance for the current tenant.
 * On first call, fetches the publishable key from the API.
 * ConnectJS calls fetchClientSecret each time it needs a session.
 *
 * @returns {Promise<object>} The ConnectJS instance
 */
export async function getStripeConnectInstance() {
    if (_instance) return _instance;

    // First call: get the publishable key (stable per platform, only need it once)
    if (!_publishableKey) {
        const response = await api.post('/payment-settings/account-session');
        _publishableKey = response.data.publishableKey;
    }

    _instance = loadConnectAndInitialize({
        publishableKey: _publishableKey,
        fetchClientSecret: async () => {
            const response = await api.post('/payment-settings/account-session');
            return response.data.clientSecret;
        },
        appearance: {
            overlays: 'dialog',
            variables: {
                colorPrimary: '#1a237e'
            }
        }
    });

    return _instance;
}

/**
 * Resets the cached instance (e.g., after tenant switch or logout).
 */
export function resetStripeConnectInstance() {
    _instance = null;
    _publishableKey = null;
}
